import { colorsPalettes } from "@similarweb/styles";

import { isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import type { BADGE_ITEMS } from "app/components/sidebar3/types";
import { openHooksModalV5 } from "app/services/ModalService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { Availability } from "./tabsUtils";
import {
    getAvailabilityInfoForBounceRate,
    getAvailabilityInfoForDeduplicatedAudience,
    getAvailabilityInfoForNewVsReturning,
    getAvailabilityInfoForUniqueUsers,
} from "./tabsUtils";

export type TabType = "traffic" | "engagement";

type Tab = {
    name: string;
    getTitle?: (is28d: boolean) => string;
    title?: string;
    iconName: string;
    format?: string;
    tooltip: string | ((availability: Availability) => string);
    type: TabType | string;
    supportInsights?: boolean;
    cache?: boolean;
    isAvailable?: (availability: Availability) => boolean;
    isLocked?: boolean;
    labelText?: BADGE_ITEMS;
    id?: string;
    apiController?: string;
    openHookModal?: () => void;
    metricDescription?: string;
    value?: number | string;
    metric?: string;
    disableCombined?: boolean;
};

type Tabs = Tab[];

export const NEW_VS_RETURNING_TAB_NAME = "NewVsReturning";
export const UNIQUE_USERS_TAB_NAME = "UniqueUsers";
// Tabs configuration
export const tabs: Tabs = [
    {
        name: "Visits",
        getTitle: (is28d: boolean) => (is28d ? "wa.ao.graph.avgvisitsdaily" : "wa.ao.graph.avgvisits"),
        iconName: "visits",
        format: "minVisitsAbbr",
        tooltip: "wa.ao.graph.avgvisits.tooltip",
        type: "traffic",
        supportInsights: true,
    },
    {
        name: UNIQUE_USERS_TAB_NAME,
        getTitle: (is28d: boolean) => (is28d ? "Wa.Ao.Graph.Muv.Window" : "wa.ao.graph.muv"),
        iconName: "monthly-unique-visitors",
        format: "minVisitsAbbr",
        cache: false,
        tooltip: (availability) => {
            const { isAvailable, reason } = getAvailabilityInfoForUniqueUsers(availability);
            return isAvailable ? "wa.ao.graph.muv.tooltip" : reason;
        },
        isAvailable: (availability) => getAvailabilityInfoForUniqueUsers(availability).isAvailable,
        type: "traffic",
    },
    {
        name: NEW_VS_RETURNING_TAB_NAME,
        title: "wa.ao.graph.new.vs.returning",
        iconName: "share-new",
        tooltip: (availability) => {
            const { isAvailable, reason } = getAvailabilityInfoForNewVsReturning(availability);
            return isAvailable ? "wa.ao.graph.new.vs.returning.tooltip" : reason;
        },
        type: "traffic",
        labelColor: colorsPalettes.orange[400],
        isAvailable: (availability) => getAvailabilityInfoForNewVsReturning(availability).isAvailable,
        get isLocked() {
            return isLocked(swSettings.components.WebNewVsReturning);
        },
        value: 52,
        disableCombined: true,
        openHookModal: () => openHooksModalV5("newVsReturning"),
        get metricDescription() {
            const { webSource } = proNavigator.getParams();
            if (webSource !== devicesTypes.TOTAL) {
                return null;
            }
            return "* Desktop only";
        },
    },
    {
        name: "DeduplicatedAudience",
        title: "wa.ao.graph.dedup",
        iconName: "users-tab",
        format: "minVisitsAbbr",
        tooltip: (availability) => {
            const { isAvailable, reason } = getAvailabilityInfoForDeduplicatedAudience(availability);
            return isAvailable ? "wa.ao.graph.dedup.tooltip" : reason;
        },
        get isLocked() {
            return isLocked(swSettings.components.WebDedup.resources);
        },
        openHookModal: () => openHooksModalV5("DeduplicatedAudience"),
        isAvailable: (availability) => getAvailabilityInfoForDeduplicatedAudience(availability).isAvailable,
        type: "traffic",
    },
    {
        name: "AvgVisitDuration",
        getTitle: (is28d: boolean) => (is28d ? "Wa.Ao.Graph.Avgduration" : "wa.ao.graph.avgduration"),
        iconName: "avg-visit-duration",
        format: "time",
        id: "AvgVisitDuration",
        apiController: "TrafficAndEngagement",
        tooltip: "wa.ao.graph.avgduration.tooltip",
        // Currently we want to hide this metric in case beta calculation is provided due to broken data in DAG
        isAvailable: ({ showBetaBranchData }) => !showBetaBranchData,
        type: "engagement",
    },
    {
        name: "PagesPerVisit",
        getTitle: (is28d: boolean) => (is28d ? "Wa.Ao.Graph.Pages" : "wa.ao.graph.pages"),
        iconName: "pages-per-visit",
        format: "decimalNumber",
        tooltip: "wa.ao.graph.pages.tooltip",
        // Currently we want to hide this metric in case beta calculation is provided due to broken data in DAG
        isAvailable: ({ showBetaBranchData }) => !showBetaBranchData,
        type: "engagement",
    },
    {
        name: "BounceRate",
        getTitle: (is28d: boolean) => (is28d ? "Wa.Ao.Graph.Bounce" : "wa.ao.graph.bounce"),
        iconName: "bounce-rate-2",
        format: "percentagesign:2",
        tooltip: (availability) => {
            const { isAvailable, reason } = getAvailabilityInfoForBounceRate(availability);
            return isAvailable ? "wa.ao.graph.bounce.tooltip" : reason;
        },
        isAvailable: (availability) => getAvailabilityInfoForBounceRate(availability).isAvailable,
        type: "engagement",
    },
    {
        name: "PageViews",
        getTitle: () => "wa.ao.graph.PageViews",
        iconName: "pages-per-visit",
        format: "minVisitsAbbr",
        tooltip: "wa.ao.graph.page.views.tooltip",
        // Currently we want to hide this metric in case beta calculation is provided due to broken data in DAG
        isAvailable: ({ showBetaBranchData }) => !showBetaBranchData,
        type: "engagement",
    },
].map((item) => ({
    ...item,
    metric: item.name,
}));

// POP items for single mode
export const initPOPItemsSingleMode: Tabs = [
    {
        name: "Visits",
        title: "wa.ao.graph.avgvisits",
        iconName: "visits",
        format: "minVisitsAbbr",
        tooltip: "wa.ao.graph.avgvisits.tooltip",
        type: "traffic",
    },
    {
        name: "AvgVisitDuration",
        title: "wa.ao.graph.avgduration",
        iconName: "avg-visit-duration",
        format: "time",
        tooltip: "wa.ao.graph.avgduration.tooltip",
        type: "engagement",
    },
    {
        name: "PagesPerVisit",
        title: "wa.ao.graph.pages",
        iconName: "pages-per-visit",
        format: "decimalNumber",
        tooltip: "wa.ao.graph.pages.tooltip",
        type: "engagement",
    },
    {
        name: "BounceRate",
        title: "wa.ao.graph.bounce",
        iconName: "bounce-rate-2",
        format: "percentagesign:2",
        tooltip: "wa.ao.graph.bounce.tooltip",
        type: "engagement",
    },
].map((item) => ({
    ...item,
    metric: item.name,
}));

// POP items for compare mode
export const initPOPItemsCompareMode: Tabs = [
    {
        name: "Visits",
        title: "wa.ao.graph.avgvisits",
        iconName: "visits",
        tooltip: "wa.ao.graph.avgvisits.tooltip",
        type: "traffic",
    },
    {
        name: UNIQUE_USERS_TAB_NAME,
        title: "wa.ao.graph.muv",
        iconName: "monthly-unique-visitors",
        tooltip: "wa.ao.graph.muv.tooltip",
        isAvailable: ({ isGa, duration }) => !isGa || duration !== "28d",
        type: "traffic",
    },
    {
        name: "AvgVisitDuration",
        title: "wa.ao.graph.avgduration",
        iconName: "avg-visit-duration",
        tooltip: "wa.ao.graph.avgduration.tooltip",
        type: "engagement",
    },
    {
        name: "PagesPerVisit",
        title: "wa.ao.graph.pages",
        iconName: "pages-per-visit",
        tooltip: "wa.ao.graph.pages.tooltip",
        type: "engagement",
    },
    {
        name: "BounceRate",
        title: "wa.ao.graph.bounce",
        iconName: "bounce-rate-2",
        tooltip: "wa.ao.graph.bounce.tooltip",
        type: "engagement",
    },
].map((item) => ({
    ...item,
    metric: item.name,
    value: " ",
    format: "NoneFilter",
}));
